import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, Loader2, PieChart } from 'lucide-react';
import { fetchRecruitmentDashboard } from '../services/recruitmentDashboardApi';
import type { RecruitmentDashboardPayload } from '../recruitmentDashboard.types';
import {
  loadReportingRatios,
  saveReportingRatios,
  REPORTING_RATIO_METRICS,
  computeRatioDisplay,
  type ReportingRatioDefinition,
  type ReportingRatioFormat,
} from '../services/reportingRatios';

interface ReportingRatiosProps {
  user: any;
}

function periodIso(previewPeriod: string): { dateFrom: string; dateTo: string } {
  const to = new Date();
  const dateTo = to.toISOString();
  const from = new Date(to);
  if (previewPeriod === '30d') from.setDate(from.getDate() - 30);
  else if (previewPeriod === '90d') from.setDate(from.getDate() - 90);
  else if (previewPeriod === 'year') from.setFullYear(from.getFullYear() - 1);
  else from.setDate(from.getDate() - 30);
  return { dateFrom: from.toISOString(), dateTo };
}

const ReportingRatios: React.FC<ReportingRatiosProps> = ({ user }) => {
  const tenantId = user?.clientId || user?.recruiterProfile?.clientId || null;
  const [rows, setRows] = useState<ReportingRatioDefinition[]>(() => loadReportingRatios(tenantId));

  const [previewPeriod, setPreviewPeriod] = useState('90d');
  const [dashboard, setDashboard] = useState<RecruitmentDashboardPayload | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  useEffect(() => {
    setRows(loadReportingRatios(tenantId));
  }, [tenantId]);

  useEffect(() => {
    const onSave = () => setRows(loadReportingRatios(tenantId));
    window.addEventListener('werftools-reporting-ratios-saved', onSave);
    window.addEventListener('storage', onSave);
    return () => {
      window.removeEventListener('werftools-reporting-ratios-saved', onSave);
      window.removeEventListener('storage', onSave);
    };
  }, [tenantId]);

  const persist = useCallback(
    (next: ReportingRatioDefinition[]) => {
      saveReportingRatios(tenantId, next);
      setRows(next);
    },
    [tenantId]
  );

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setDashboardLoading(true);
      setDashboardError(null);
      try {
        const isClientPortal =
          user?.role === 'CLIENT' ||
          window.location.hostname.toLowerCase().includes('clientportal') ||
          new URLSearchParams(window.location.search).get('clientportal') === 'true';
        const { dateFrom, dateTo } = periodIso(previewPeriod);
        const portalScope = String(user?.clientId || user?.client_id || '').trim();
        const filters: Record<string, unknown> = {
          dateFrom,
          dateTo,
          useEndClient: isClientPortal,
          ...(isClientPortal && portalScope ? { clientId: portalScope } : {}),
        };
        const secret = (import.meta as any).env?.VITE_RECRUITMENT_DASHBOARD_SECRET as string | undefined;
        const data = await fetchRecruitmentDashboard(filters as any, { secret });
        if (!cancelled) setDashboard(data);
      } catch (e: any) {
        if (!cancelled) {
          setDashboard(null);
          setDashboardError(e?.message || 'Dashboard niet bereikbaar');
        }
      } finally {
        if (!cancelled) setDashboardLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [previewPeriod, user?.role, user?.clientId, user?.client_id]);

  const addRow = () => {
    persist([
      ...rows,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: '',
        numeratorMetricId: 'jobsAdded',
        denominatorMetricId: 'cvsSent',
        format: 'normalised',
      },
    ]);
  };

  const removeRow = (id: string) => persist(rows.filter((r) => r.id !== id));

  const patchRow = (id: string, patch: Partial<ReportingRatioDefinition>) =>
    persist(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const metricLabel = (id: string) => REPORTING_RATIO_METRICS.find((m) => m.id === id)?.label ?? id;

  return (
    <div className="h-full overflow-y-auto bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-4 md:p-8">
      <div className="mx-auto max-w-4xl space-y-10">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 shadow-lg shadow-indigo-500/25">
            <PieChart className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-900">Ratio&apos;s — Rapportage</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Definieer welke verhoudingen je wilt tonen (genormaliseerd als <strong className="font-semibold text-slate-800">1:X</strong>{' '}
              of als <strong className="font-semibold text-slate-800">percentage</strong>). Recruitment-metrics komen uit het
              dashboard; prompt-metrics zijn placeholders tot er een databron wordt gekoppeld.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-6 shadow-xl shadow-slate-200/50">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600">Configure ratios</h2>
            <button
              type="button"
              onClick={addRow}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-dashed border-indigo-300 bg-indigo-50/80 px-4 py-2 text-xs font-black uppercase tracking-wide text-indigo-700 hover:bg-indigo-100"
            >
              <Plus size={16} /> Add ratio
            </button>
          </div>

          <div className="space-y-3">
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-100 bg-slate-50/80 p-3 md:flex-nowrap md:items-center"
              >
                <input
                  type="text"
                  placeholder="Ratio name"
                  value={row.name}
                  onChange={(e) => patchRow(row.id, { name: e.target.value })}
                  className="min-w-[160px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-900"
                />
                <select
                  value={row.numeratorMetricId}
                  onChange={(e) => patchRow(row.id, { numeratorMetricId: e.target.value })}
                  className="min-w-[140px] rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-semibold text-slate-800"
                >
                  {REPORTING_RATIO_METRICS.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <span className="hidden shrink-0 text-xs font-bold text-slate-400 md:inline">to</span>
                <select
                  value={row.denominatorMetricId}
                  onChange={(e) => patchRow(row.id, { denominatorMetricId: e.target.value })}
                  className="min-w-[140px] rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-semibold text-slate-800"
                >
                  {REPORTING_RATIO_METRICS.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <select
                  value={row.format}
                  onChange={(e) => patchRow(row.id, { format: e.target.value as ReportingRatioFormat })}
                  className="min-w-[120px] rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-semibold"
                >
                  <option value="normalised">Normalised</option>
                  <option value="percentage">Percentage</option>
                </select>
                <button
                  type="button"
                  onClick={() => removeRow(row.id)}
                  className="shrink-0 rounded-lg p-2 text-red-400 hover:bg-red-50 hover:text-red-600"
                  title="Remove"
                >
                  <X size={18} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-200/80 bg-white/90 p-6 shadow-xl">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600">Live preview</h2>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold text-slate-500">Period</span>
              <select
                value={previewPeriod}
                onChange={(e) => setPreviewPeriod(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-800"
              >
                <option value="30d">30 days</option>
                <option value="90d">90 days</option>
                <option value="year">Year</option>
              </select>
              {dashboardLoading && <Loader2 size={14} className="animate-spin text-emerald-600" />}
            </div>
          </div>
          {dashboardError && (
            <p className="mb-3 text-sm text-amber-700">{dashboardError} — ratios tonen recruitment-KPI’s niet.</p>
          )}
          {dashboard && (
            <div className="overflow-x-auto rounded-xl border border-slate-100">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">Ratio</th>
                    <th className="px-4 py-3">First metric</th>
                    <th className="px-4 py-3">Second metric</th>
                    <th className="px-4 py-3">Format</th>
                    <th className="px-4 py-3">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-semibold text-slate-900">{row.name || '(unnamed)'}</td>
                      <td className="px-4 py-3 text-slate-600">{metricLabel(row.numeratorMetricId)}</td>
                      <td className="px-4 py-3 text-slate-600">{metricLabel(row.denominatorMetricId)}</td>
                      <td className="px-4 py-3 capitalize text-slate-500">{row.format}</td>
                      <td className="px-4 py-3 font-mono font-bold text-emerald-700">
                        {computeRatioDisplay(row, dashboard.kpis)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportingRatios;
